import type { ElementContext } from './contextTypes.ts'

export const BUNDLE_VERSION = 1

export const MAX_EVENTS = 3000
export const MAX_SELECTION_CHARS = 1000
/** Stays well under the 1 MB database check on the stored bundle. */
export const MAX_BUNDLE_BYTES = 900_000

type StepEvent = { type: 'step'; atMs: number; name: string; role: string; route: string } & ElementContext
type PointerEvent = { type: 'pointer'; atMs: number; name: string; role: string; route: string; restMs: number; x: number; y: number } & ElementContext
type SelectionEvent = { type: 'selection'; atMs: number; name: string; text: string; route?: string } & ElementContext
type RequestEvent = { type: 'request'; atMs: number; method: string; path: string; status: number; durationMs: number; correlationId?: string; errorCode?: string }
type ConsoleEvent = { type: 'console'; atMs: number; level: 'error' | 'warn'; source: string; count: number }
export type BundleEvent = StepEvent | PointerEvent | SelectionEvent | RequestEvent | ConsoleEvent

export interface Bundle {
  version: 1
  app: 'consultant'
  route: string
  durationMs: number
  audio: { mimeType: string; bytes: number }
  events: BundleEvent[]
  /** The commit the page was built from, and the window size: both let an agent reproduce what the user saw. */
  release?: string
  viewport?: { w: number; h: number }
  /** Wall-clock start (epoch ms, browser clock): adds an event's `atMs` to reach the same instant in the Sentry replay, which uses the same clock. */
  startedAt?: number
  dropped: { events: number; requests: number; console: number }
}

export type BundleErrorCode = 'BUNDLE_VERSION_UNSUPPORTED' | 'BUNDLE_INVALID'
export type ValidationResult =
  | { ok: true; bundle: Bundle }
  | { ok: false; code: BundleErrorCode; message: string }

const fail = (code: BundleErrorCode, message: string): ValidationResult => ({ ok: false, code, message })
const invalid = (why: string) => fail('BUNDLE_INVALID', `The recording could not be read (${why}). Try again.`)

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isStr = (v: unknown): v is string => typeof v === 'string'
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

// Exact key sets per event type: an extra key (body, headers, query...) is refused rather than stored.
const CONTEXT_KEYS = ['tag', 'chain', 'position', 'label', 'labelKey', 'src', 'viaTrigger', 'attrs', 'box'] as const
const SRC_LOCATION = /^[\w./-]{1,160}\.tsx:\d{1,6}$/
const LABEL_KEY = /^[\w.-]{1,160}$/
const EVENT_KEYS: Record<string, readonly string[]> = {
  step: ['type', 'atMs', 'name', 'role', 'route', ...CONTEXT_KEYS],
  pointer: ['type', 'atMs', 'name', 'role', 'route', 'restMs', 'x', 'y', ...CONTEXT_KEYS],
  selection: ['type', 'atMs', 'name', 'text', 'route', ...CONTEXT_KEYS],
  request: ['type', 'atMs', 'method', 'path', 'status', 'durationMs', 'correlationId', 'errorCode'],
  console: ['type', 'atMs', 'level', 'source', 'count'],
}

const onlyKeys = (v: Record<string, unknown>, keys: readonly string[]) => Object.keys(v).every((k) => keys.includes(k))
const allNums = (v: Record<string, unknown>) => Object.values(v).every(isNum)

/** The element context is optional (older pages do not send it), but when present it must be exactly this shape. */
function contextProblem(e: Record<string, unknown>): string | null {
  if ('tag' in e && !(isStr(e.tag) && e.tag.length <= 30)) return 'bad element tag'
  if ('label' in e && !(isStr(e.label) && e.label.length <= 80)) return 'bad element label'
  if ('labelKey' in e && !(isStr(e.labelKey) && LABEL_KEY.test(e.labelKey))) return 'bad label key'
  if ('src' in e && !(isStr(e.src) && SRC_LOCATION.test(e.src))) return 'bad source location'
  if ('viaTrigger' in e && e.viaTrigger !== true) return 'bad viaTrigger'
  if ('chain' in e) {
    const chain = e.chain
    if (!Array.isArray(chain) || chain.length > 8) return 'bad component chain'
    for (const entry of chain) {
      if (!isObj(entry) || !onlyKeys(entry, ['component', 'file', 'element'])) return 'bad component chain entry'
      if (![entry.component, entry.file, entry.element].every((v) => isStr(v) && v.length <= 200)) return 'bad component chain entry'
    }
  }
  if ('position' in e && !(isObj(e.position) && onlyKeys(e.position, ['n', 'of', 'row', 'rows', 'col', 'cols']) && allNums(e.position))) return 'bad position'
  if ('box' in e && !(isObj(e.box) && onlyKeys(e.box, ['x', 'y', 'w', 'h']) && allNums(e.box))) return 'bad box'
  if ('attrs' in e) {
    const attrs = e.attrs
    if (!isObj(attrs) || Object.keys(attrs).length > 16 || !Object.values(attrs).every((v) => isStr(v) && v.length <= 200)) return 'bad attributes'
  }
  return null
}

function eventProblem(e: unknown): string | null {
  if (!isObj(e) || !isStr(e.type) || !(e.type in EVENT_KEYS)) return 'unknown event type'
  if (!isNum(e.atMs) || e.atMs < 0) return 'bad event time'
  if (Object.keys(e).some((k) => !EVENT_KEYS[e.type as string].includes(k))) return 'unexpected field'
  switch (e.type) {
    case 'step':
      return isStr(e.name) && isStr(e.role) && isStr(e.route) ? contextProblem(e) : 'bad step'
    case 'pointer':
      return isStr(e.name) && isStr(e.role) && isStr(e.route) && isNum(e.restMs) && isNum(e.x) && isNum(e.y) ? contextProblem(e) : 'bad pointer'
    case 'selection':
      return isStr(e.name) && isStr(e.text) && e.text.length <= MAX_SELECTION_CHARS && (!('route' in e) || isStr(e.route)) ? contextProblem(e) : 'bad selection'
    case 'request':
      return isStr(e.method) && isStr(e.path) && !e.path.includes('?') && isNum(e.status) && isNum(e.durationMs) ? null : 'bad request'
    case 'console':
      return (e.level === 'error' || e.level === 'warn') && isStr(e.source) && isNum(e.count) ? null : 'bad console entry'
    default:
      return 'unknown event type'
  }
}

export function validateBundle(input: unknown): ValidationResult {
  if (!isObj(input)) return invalid('not an object')
  if (input.version !== BUNDLE_VERSION) {
    return fail('BUNDLE_VERSION_UNSUPPORTED', 'This page is out of date. Please refresh and record again.')
  }
  if (input.app !== 'consultant' || !isStr(input.route)) return invalid('missing app or route')
  if (!isNum(input.durationMs) || input.durationMs <= 0) return invalid('bad duration')
  const audio = input.audio
  if (!isObj(audio) || !isStr(audio.mimeType) || !isNum(audio.bytes) || audio.bytes <= 0) return invalid('bad audio')
  const dropped = input.dropped
  if (!isObj(dropped) || !isNum(dropped.events) || !isNum(dropped.requests) || !isNum(dropped.console)) return invalid('bad dropped counts')
  if ('release' in input && !(isStr(input.release) && input.release.length <= 64)) return invalid('bad release')
  if ('viewport' in input && !(isObj(input.viewport) && onlyKeys(input.viewport, ['w', 'h']) && allNums(input.viewport))) return invalid('bad viewport')
  if ('startedAt' in input && !(isNum(input.startedAt) && input.startedAt > 0)) return invalid('bad start time')
  if (!Array.isArray(input.events) || input.events.length > MAX_EVENTS) return invalid('bad event list')

  let last = -1
  for (const e of input.events) {
    const problem = eventProblem(e)
    if (problem) return invalid(problem)
    const at = (e as { atMs: number }).atMs
    if (at < last) return invalid('events out of order')
    last = at
  }
  return { ok: true, bundle: input as unknown as Bundle }
}
